import { writeDB, api } from './store';
import { Shop, getShopName } from './shop';
import { history } from './history';
import { categoryPath, categoriesPath } from '../routes';
import { ProductCategory } from './product';
import { PaginatedLoaderResponse, paginatedLoaderResponseFrom, GetParams } from './data';
import { BasePermissions } from './permission';
import { Image, FileRepresenting } from './asset';
import { PaginatedResponse } from './api';
import { t } from '../locales/translate';
import { apollo, queryAllPages } from './graphql';
import { SEARCH_CATEGORIES, GET_CATEGORIES, GET_PRODUCT_CATEGORIES } from '../model/queries/categories';
import { compact } from 'lodash';

/* INTERFACES */
export type CategoryType = 'artist' | 'category' | 'brand' | 'label' | 'tour';

export const categoryTypes: CategoryType[] = ['artist', 'category', 'brand', 'label', 'tour'];

export interface CategoryBase {
  slug?: string | null;
  name?: string;
  nameDe?: string | null;
  nameEn?: string | null;
  description?: string | null;
  abstract?: string;
  createdAt?: Date;
  subtitle?: string;
  subtitleInShop?: boolean;
  featuredAt?: Date;
  id: string;
  mainCategory: boolean;
  parentCategoryId?: string;
  internalName?: string;
  shop?: Shop;
  shopId: string;
  subcategories?: Category[];
  type: CategoryType | null;
  updatedAt: Date;
  $permissions: BasePermissions[];
  order?: number | null;
  sortOrderHomepage?: number | null;
  sortOrderTicketTopLeft?: number | null;
  sortOrderTicketTopRight?: number | null;
  sortOrderTicketBottom?: number | null;
}

export interface Category extends CategoryBase {
  squareImage?: Image;
  portraitImage?: Image;
  landscapeImage?: Image;
  alternativeNames?: string[];
  parentCategory?: Category;
  meta?: Record<string, JSONValue>;
}

export interface CategoryEdit extends CategoryBase {
  squareImage?: FileRepresenting | null;
  portraitImage?: FileRepresenting | null;
  landscapeImage?: FileRepresenting | null;
  alternativeNames?: string | null;
}

export interface OrderedCategory {
  order: number | null;
  category: Category;
  urlInShop?: string;
}

interface CategoryNameFormattingOptions {
  withShop?: boolean;
  preferInternalName?: boolean;
  includeShopDomain?: boolean;
}

/* CATEGORIES CREATE */
export async function addCategory(category: Partial<CategoryEdit>): Promise<void> {
  const response = (
    await api.createCategory({
      ...category,
      alternativeNames: category.alternativeNames?.split(',').map((e) => e.trim()),
    })
  ).data;
  writeDB.table('categories').upsert(response);
  history.push(categoryPath(response as Category));
}

/* CATEGORIES READ */
export async function refreshCategories(options: { shop: Shop }): Promise<void> {
  const categories = await api.indexCategories({});
  writeDB.table('categories').upsert(categories.data.map((e) => ({ ...e, shopId: options.shop.id })));
}

export async function getRawCategories({ page, perPage, term }: GetParams): Promise<PaginatedResponse<Category[]>> {
  const response = await api.indexCategories({
    page: (page || 0) + 1,
    perPage,
    term,
  });
  writeDB.table('categories').upsert(response.data);
  return response as PaginatedResponse<Category[]>;
}

export async function getCategories({
  types,
  page,
  perPage,
  term,
  sortBy = 'name',
  sortDir = 'desc',
}: {
  types: Array<'artist' | 'category' | 'brand' | 'label' | 'tour'>;
  page: number;
  perPage: number;
  term?: string;
  sortBy?: string;
  sortDir?: string;
}): Promise<PaginatedLoaderResponse> {
  const response = await api.indexCategories({
    page: (page || 0) + 1,
    perPage,
    types,
    term,
    sortDir,
    sortBy,
  });
  writeDB.table('categories').upsert(response.data);
  return paginatedLoaderResponseFrom(response);
}

function getCategoryNameWithType(category?: Category, preferInternalName: boolean = false): string | null {
  if (!category) return null;

  const type_desc = category.type ? t.model[category.type as CategoryType]?._name || category.type : '';
  const name = preferInternalName ? category.internalName || category.name : category.name;
  return `${name} [${type_desc}]`;
}

export function getCategoryName(
  categoryOrId?: string | Category,
  formattingOptions: CategoryNameFormattingOptions = {}
): string | undefined {
  if (!categoryOrId) {
    return undefined;
  }
  if (typeof categoryOrId === 'string') {
    const category = writeDB.table('categories').find(categoryOrId);
    return category ? formatCategoryName(category, formattingOptions) : categoryOrId;
  }
  return formatCategoryName(categoryOrId, formattingOptions);
}

function formatCategoryName(
  category: Category,
  { withShop, preferInternalName, includeShopDomain }: CategoryNameFormattingOptions = {}
): string | undefined {
  const parentCategory =
    category.parentCategory ||
    (category.parentCategoryId && writeDB.table('categories').find(category.parentCategoryId)) ||
    undefined;
  return [
    withShop && category.shop
      ? `${category.shop.name}${includeShopDomain ? ` [${category.shop.domain || category.shop.slug}]` : ''}`
      : category.parentCategoryId && getShopName(category.parentCategoryId),
    getCategoryNameWithType(parentCategory, preferInternalName),
    getCategoryNameWithType(category, preferInternalName),
  ]
    .filter(Boolean)
    .join(' > ');
}

export async function getRawCategory(categoryId: string): Promise<PaginatedResponse<Category>> {
  const response = await api.showCategory({ categoryId });
  writeDB.table('categories').upsert(response.data);
  return response as PaginatedResponse<Category>;
}

export async function getCategory(categoryId: string): Promise<void> {
  const category = (await api.showCategory({ categoryId })).data;
  writeDB.table('categories').upsert(category);
}

/* CATEGORIES UPDATE */
export async function patchCategory(category: Partial<CategoryEdit>): Promise<void> {
  if (category.id) {
    const response = await api.updateCategory({
      categoryId: category.id,
      ...category,
      alternativeNames: category.alternativeNames?.split(',').map((e) => e.trim()),
    });

    // TODO: Update api model to match local interface
    const data = response.data as unknown as Category;
    writeDB.table('categories').update(category.id, data);
  }
}

/* CATEGORIES DELETE */
export async function deleteCategory(categoryId: string): Promise<void> {
  await api.destroyCategory({ categoryId });
  writeDB.table('categories').delete(categoryId);
  history.push(categoriesPath());
}

/* CATEGORIES ASSIGNMENT CREATE */
export async function addCategoryAssignment({
  productId,
  categoryId,
  order,
}: {
  productId: string;
  categoryId: string;
  order: number | null;
}): Promise<ProductCategory> {
  const response = await api.createCategoryAssignment({
    productId,
    order,
    categoryId,
  });
  return response.data as ProductCategory;
}

/* CATEGORIES ASSIGNMENT UPDATE */
export async function patchCategoryAssignment({
  productId,
  categoryId,
  order,
}: {
  productId: string;
  categoryId: string;
  order: number | null;
}): Promise<ProductCategory> {
  const response = await api.updateCategoryAssignment({
    productId,
    categoryId,
    order,
  });
  return response.data as ProductCategory;
}

/* CATEGORIES ASSIGNMENT DELETE */
export async function deleteCategoryAssignment(productId: string, categoryId: string): Promise<void> {
  await api.destroyCategoryAssignment({
    productId,
    categoryId,
  });
}

// GRAPHQL
export async function searchCategories({
  term,
  types,
  shopId,
}: {
  term: string;
  types: CategoryType[] | null;
  shopId: string | null;
}): Promise<PaginatedResponse<Category[]>> {
  const result = await apollo.query({
    query: SEARCH_CATEGORIES,
    variables: { terms: term.split('&'), types, shopId },
    fetchPolicy: 'network-only',
  });

  const data = compact(result.data.categories.nodes || []);

  writeDB.table('categories').upsert(data);

  return {
    data,
  } as unknown as PaginatedResponse<Category[]>;
}

export async function getCategoriesByIds(ids: string[]): Promise<Category[]> {
  const result = await apollo.query({
    query: GET_CATEGORIES,
    variables: { ids },
    fetchPolicy: 'network-only',
  });

  const data = compact(result.data.categories.nodes || []);

  writeDB.table('categories').upsert(data);

  return data as unknown as Category[];
}

export async function getProductCategories(id: string): Promise<void> {
  const categories = await queryAllPages({
    query: GET_PRODUCT_CATEGORIES,
    variables: { id },
    elementExtractor: (r) =>
      compact(r.data.product?.categoryProducts.edges?.map((e) => e?.node)).map((e) => e as ProductCategory),
    pageInfoExtractor: (r) => r.data.product?.categoryProducts.pageInfo,
  });
  if (categories.length) {
    writeDB.table('products').update(id, { categories });
  }
}
